import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import dayjs from 'dayjs';

import type { IRootStore } from '@stores/index';
import { getTrialPhase } from '@components/trial/utils';
import {
  TrialStatus,
  OrganizationTrial,
  getOrgTrials,
  startOrgTrial,
  BillingPlan,
  TrialDefinition,
  getAvailableTrials,
} from '@api';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { ServerFeatures } from './features';

type TrialPhase =
  | 'active_safe'
  | 'active_warning'
  | 'active_critical'
  | 'grace_period'
  | 'hard_stop';

interface FeatureTrialInfo {
  daysRemaining: number;
  phase: TrialPhase;
  trial: OrganizationTrial;
}

export class TrialStore {
  @observable public rootStore: IRootStore;

  @observable public orgTrials: OrganizationTrial[] = [];

  @observable public availableTrials: TrialDefinition[] = [];

  @observable public loading = false;

  @observable loaded = false;

  private async loadOrgTrials() {
    const orgId = this.rootStore.organizationStore.id;
    if (!orgId) {
      return;
    }
    const trials = await getOrgTrials(orgId);
    runInAction(() => {
      this.orgTrials = trials.data;
    });
  }

  @computed get hasActiveTrials() {
    return this.activeTrials.length > 0;
  }

  @action private setAvailableTrials(trials: TrialDefinition[]) {
    this.availableTrials = trials;
  }

  private async getAvailableTrial() {
    const orgId = this.rootStore.organizationStore.id;
    if (!orgId) {
      return;
    }
    const trials = await getAvailableTrials(orgId);
    this.setAvailableTrials(trials.data);
  }

  @computed get availablePlanTrialToStart() {
    return this.availableTrials.find((def) => def.trialType === 'plan');
  }

  async load(force = false) {
    const { featuresStore, messagesStore } = this.rootStore;
    const isEnabled =
      experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrial) && featuresStore.has('BILLING');
    if (!isEnabled || (!force && (this.loaded || this.loading))) {
      return;
    }
    try {
      this.loading = true;
      await this.loadOrgTrials();
      this.loaded = true;
    } catch (error) {
      messagesStore.showErrorMessage((error as Error)?.message);
    } finally {
      this.loading = false;
    }
    if (this.loaded && experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrialExistingUsers)) {
      try {
        await this.getAvailableTrial();
      } catch (error) {
        messagesStore.showErrorMessage((error as Error)?.message);
      }
    }
  }

  async startTrial() {
    const { organizationStore, messagesStore, featuresStore } = this.rootStore;
    try {
      await startOrgTrial(organizationStore.id);
      this.setShowWelcomeModal(false);

      this.reset();
      await Promise.all([featuresStore.getFeatures(), this.load()]);
    } catch (error) {
      messagesStore.showErrorMessage((error as Error)?.message);
    }
  }

  @observable public showWelcomeModal: boolean | undefined = undefined;

  @action setShowWelcomeModal(value: boolean) {
    this.showWelcomeModal = value;
  }

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @computed get activeTrials(): OrganizationTrial[] {
    return this.orgTrials?.filter((t) =>
      [TrialStatus.IN_PROGRESS, TrialStatus.GRACE_PERIOD].includes(t.status),
    );
  }

  @computed get planTrial(): OrganizationTrial | undefined {
    return this.activeTrials.find((t) => t.definition.trialType === 'plan');
  }

  @computed get isPlanTrialInActivePeriod(): boolean {
    return this.planTrial?.status === TrialStatus.IN_PROGRESS;
  }

  @computed get isPlanTrialInGracePeriod(): boolean {
    return this.planTrial?.status === TrialStatus.GRACE_PERIOD;
  }

  @computed get isOnTrial(): boolean {
    return !!this.planTrial;
  }

  @computed get trialDaysRemaining(): number | null {
    if (!this.planTrial) {
      return null;
    }
    return Math.ceil(dayjs(this.planTrial.endsAt).diff(dayjs(), 'day', true));
  }

  @computed get trialTargetPlan(): BillingPlan | null {
    if (!this.planTrial) {
      return null;
    }
    return this.planTrial.definition.plan;
  }

  @computed get trialPhase(): TrialPhase | null {
    if (this.trialDaysRemaining === null) {
      return null;
    }
    return getTrialPhase(this.trialDaysRemaining);
  }

  static featureMapping: Record<string, string> = {
    WAREHOUSE_SSH_TUNNELLING: 'WAREHOUSE_TUNNELLING',
  };

  /**
   * Compares a single feature value between the trial plan and the current plan.
   * Returns true if the trial enhances the feature:
   * - Boolean: trial has it, current plan doesn't
   * - Number: trial value is higher than current plan value
   * - Current plan missing the feature entirely
   */
  private isFeatureEnhanced(
    featureName: string,
    trialFeatures: Record<string, unknown>,
    currentPlanFeatures: Record<string, unknown>,
  ): boolean {
    const featureKey = TrialStore.featureMapping[featureName] || featureName;
    const trialValue = trialFeatures[featureKey];
    const currentValue = currentPlanFeatures[featureKey];

    if (trialValue === currentValue) {
      return false;
    }

    // Feature not in current plan at all → trial enhances it
    if (!(featureName in currentPlanFeatures)) {
      return true;
    }

    // Feature not in trial plan but exists as a number in current plan → trial is unlimited
    if (!(featureName in trialFeatures) && typeof currentValue === 'number') {
      return true;
    }

    // Numeric limits: trial is higher
    if (typeof trialValue === 'number' && typeof currentValue === 'number') {
      return trialValue > currentValue;
    }

    // Boolean: trial enables it, current plan doesn't
    if (typeof trialValue === 'boolean') {
      return trialValue === true && currentValue !== true;
    }

    // Fallback: different truthy values
    return !!trialValue && !currentValue;
  }

  /**
   * Resolves the effective features for a trial.
   * Merges definition.plan.features with definition.features overrides
   * (definition.features takes precedence).
   */
  getTrialEffectiveFeatures(trial?: OrganizationTrial): Record<string, unknown> | null {
    const trialToUse = trial ?? this.planTrial;
    const planFeatures = trialToUse?.definition.plan?.features;
    const definitionFeatures = trialToUse?.definition.features;

    if (!planFeatures && !definitionFeatures) {
      return null;
    }

    return { ...(planFeatures ?? {}), ...(definitionFeatures ?? {}) };
  }

  getFeatureTrialInfo(
    featureName: keyof ServerFeatures,
    { invert = false } = {},
  ): FeatureTrialInfo | null {
    const currentPlanFeatures = this.rootStore.organizationStore.orgPlan?.features ?? {};

    const trial = this.activeTrials.find((t) => {
      const trialFeatures = this.getTrialEffectiveFeatures(t);
      if (!trialFeatures) {
        return false;
      }
      if (invert) {
        // For inverted flags (false = enabled), a trial that provides false while the base
        // is true is an enhancement — isFeatureEnhanced does not cover this case.
        return !trialFeatures[featureName] && currentPlanFeatures[featureName] === true;
      }
      return this.isFeatureEnhanced(featureName, trialFeatures, currentPlanFeatures);
    });

    if (!trial) {
      return null;
    }

    const daysRemaining = Math.ceil(dayjs(trial.endsAt).diff(dayjs(), 'day', true));
    return {
      daysRemaining,
      phase: getTrialPhase(daysRemaining),
      trial,
    };
  }

  @computed private get isTrialForExistingUsersEnabled(): boolean {
    return experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrialExistingUsers);
  }

  @computed get eligibleForTrial(): boolean {
    const { billingStore, userStore, organizationStore } = this.rootStore;
    return (
      userStore.isOrgAdmin &&
      this.loaded &&
      this.orgTrials.length === 0 &&
      organizationStore.isEligibleForTrial &&
      billingStore.isFreeTier &&
      this.isTrialForExistingUsersEnabled &&
      !!this.availablePlanTrialToStart
    );
  }

  @action reset() {
    this.orgTrials = [];
    this.loaded = false;
  }
}
